import * as React from 'react'
import { AlertTriangle, Check, Loader2, RefreshCw } from 'lucide-react'
import type {
  HcpServiceDto,
  HousecallProStatusDto,
} from '@lib/dto/housecall-pro.dto'
import { formatMoney, parseMoney } from '@lib/helpers/money'
import {
  useHcpServices,
  useHousecallProStatus,
  useSyncHousecallPro,
  useUpdateHousecallPro,
} from '@/hooks/useHousecallPro'
import { HcpStatusMessage } from '@/components/HcpStatusMessage'
import { Button } from '@/components/ui/button'
import { SELECT_CLASS } from '@/components/ui/field-classes'
import { errorMessage } from '@/helpers/logger'

function formatPrice(price: string): string {
  return formatMoney(parseMoney(price) ?? 0)
}

function formatSyncedAt(syncedAt: string | null): string {
  if (syncedAt === null) return 'never'
  return new Date(syncedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/** "Commercial Irrigation labor · $127.00 · taxed" */
function laborLabel(service: HcpServiceDto): string {
  return `${service.name} · ${formatPrice(service.price)}${service.taxable ? ' · taxed' : ''}`
}

/**
 * The services worth offering as labor: anything HCP names labor, plus the
 * one already chosen, so a mapping to an oddly named service still shows.
 */
function laborOptions(
  services: readonly HcpServiceDto[],
  chosen: readonly (string | null)[],
): HcpServiceDto[] {
  return services.filter(
    (service) => /labor/i.test(service.name) || chosen.includes(service.uuid),
  )
}

/** Each category the copy's services are filed in, once, by name. */
function serviceCategories(
  services: readonly HcpServiceDto[],
): { id: number; name: string }[] {
  const categories = new Map<number, string>()
  for (const service of services) {
    if (service.categoryId !== null && !categories.has(service.categoryId)) {
      categories.set(service.categoryId, service.categoryName ?? 'Unnamed')
    }
  }
  return [...categories]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * What an irrigation repair is priced by, beyond its parts: the Housecall Pro
 * labor service for each client type, and the category the town drive
 * charges are filed in.
 *
 * Its own section, with its own Save, beside the parts one: the two are read
 * by the same sync but set for different reasons, and a person mapping labor
 * should not have to think about part pickers. Nothing is shown until the
 * connection exists; the parts section already says how to set it up.
 */
export function IrrigationPricingSettings() {
  const status = useHousecallProStatus()
  // Held here, above the key: a save remounts the form, and "Saved" has to
  // outlive that.
  const [saved, setSaved] = React.useState(false)
  if (status.isPending || status.isError) {
    return <HcpStatusMessage status={status} />
  }
  if (!status.data.configured) return null
  const stored = status.data
  return (
    <PricingForm
      // A save or sync that changes what is stored starts the form again
      // from it.
      key={[
        stored.residentialLaborService?.uuid ?? '',
        stored.commercialLaborService?.uuid ?? '',
        String(stored.driveChargeCategory?.id ?? ''),
      ].join('|')}
      status={stored}
      saved={saved}
      onSavedChange={setSaved}
    />
  )
}

function PricingForm({
  status,
  saved,
  onSavedChange,
}: {
  status: HousecallProStatusDto
  /** Whether the last save went through and nothing has changed since. */
  saved: boolean
  onSavedChange: (saved: boolean) => void
}) {
  const services = useHcpServices(true)
  const update = useUpdateHousecallPro()
  const sync = useSyncHousecallPro()
  const [residential, setResidential] = React.useState<string | null>(
    status.residentialLaborService?.uuid ?? null,
  )
  const [commercial, setCommercial] = React.useState<string | null>(
    status.commercialLaborService?.uuid ?? null,
  )
  const [categoryId, setCategoryId] = React.useState<number | null>(
    status.driveChargeCategory?.id ?? null,
  )
  const [error, setError] = React.useState<string | null>(null)

  const list = services.data?.services ?? []
  const options = laborOptions(list, [residential, commercial])
  const categories = serviceCategories(list)
  const isBusy = update.isPending || sync.isPending
  const isDirty =
    residential !== (status.residentialLaborService?.uuid ?? null) ||
    commercial !== (status.commercialLaborService?.uuid ?? null) ||
    categoryId !== (status.driveChargeCategory?.id ?? null)

  async function handleSave(): Promise<void> {
    setError(null)
    try {
      await update.mutateAsync({
        residentialLaborServiceUuid: residential,
        commercialLaborServiceUuid: commercial,
        driveChargeCategoryId: categoryId,
      })
      onSavedChange(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  async function handleSync(): Promise<void> {
    setError(null)
    try {
      await sync.mutateAsync()
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  function laborSelect(
    id: string,
    label: string,
    value: string | null,
    onChange: (uuid: string | null) => void,
  ): React.ReactNode {
    return (
      <div className="space-y-1">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        <select
          id={id}
          className={SELECT_CLASS}
          value={value ?? ''}
          disabled={isBusy || services.isPending}
          onChange={(event) => {
            onSavedChange(false)
            onChange(event.target.value === '' ? null : event.target.value)
          }}
        >
          <option value="">Not chosen</option>
          {options.map((service) => (
            <option key={service.uuid} value={service.uuid}>
              {laborLabel(service)}
            </option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">
          Irrigation labor and drive charges
        </h2>
        <p className="text-sm text-secondary-text">
          A repair is priced as its parts, plus its minutes at the client
          type&apos;s labor service, plus the drive charge for the site&apos;s
          town. Each item is taxed as Housecall Pro marks it, so the bid agrees
          with the invoice.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-default px-3 py-2">
        <p className="text-sm text-secondary-text">
          Services read {formatSyncedAt(status.servicesSyncedAt)}
        </p>
        <Button
          variant="outline"
          size="sm"
          disabled={isBusy}
          onClick={() => {
            void handleSync()
          }}
        >
          {sync.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Sync now
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {laborSelect(
          'hcp-residential-labor',
          'Residential labor',
          residential,
          setResidential,
        )}
        {laborSelect(
          'hcp-commercial-labor',
          'Commercial labor',
          commercial,
          setCommercial,
        )}
      </div>

      <div className="space-y-1">
        <label htmlFor="hcp-drive-category" className="text-sm font-medium">
          Drive charges category
        </label>
        <select
          id="hcp-drive-category"
          className={SELECT_CLASS}
          value={categoryId === null ? '' : String(categoryId)}
          disabled={isBusy || services.isPending}
          onChange={(event) => {
            onSavedChange(false)
            const chosen = categories.find(
              (category) => String(category.id) === event.target.value,
            )
            setCategoryId(chosen?.id ?? null)
          }}
        >
          <option value="">No drive charges</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-text">
          Most towns have two drive charges in Housecall Pro, one for a tree
          crew and one for an irrigation technician. Choose the category the
          irrigation ones are filed in.
        </p>
      </div>

      {status.driveCharges.length === 0 ? null : (
        <div className="space-y-1">
          <p className="text-xs font-medium text-secondary-text">
            {status.driveCharges.length} towns in{' '}
            {status.driveChargeCategory?.name}
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {status.driveCharges.map((charge) => (
              <li
                key={charge.service.uuid}
                className="rounded-full border border-default px-2 py-0.5 text-xs text-secondary-text"
              >
                {charge.town} {formatPrice(charge.service.price)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {status.duplicateDriveChargeTowns.length === 0 ? null : (
        <p className="flex gap-2 rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
          <span>
            {status.duplicateDriveChargeTowns.join(', ')}{' '}
            {status.duplicateDriveChargeTowns.length === 1 ? 'has' : 'have'}{' '}
            more than one drive charge in this category. The first is used;
            remove the extra in Housecall Pro.
          </span>
        </p>
      )}

      {status.skippedServices.length === 0 ? null : (
        <p className="flex gap-2 rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
          <span>
            Housecall Pro sent services this app could not read, so they are
            left out: {status.skippedServices.join(', ')}.
          </span>
        </p>
      )}

      {services.isError ? (
        <p className="text-sm text-danger">{errorMessage(services.error, 'That could not be saved.')}</p>
      ) : null}
      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      <div className="flex items-center gap-3 border-t border-default pt-3">
        <Button
          disabled={!isDirty || isBusy}
          onClick={() => {
            void handleSave()
          }}
        >
          {update.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save changes
        </Button>
        {saved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
    </section>
  )
}
